import { createUseStyles } from 'react-jss';
import { Spacings } from '@picsart/design-system/foundation/spacings';
import { CornerRadius } from '@picsart/design-system/foundation/cornerRadius';
import { BackgroundColors } from '@picsart/design-system/foundation/colors';
import { Shadows } from '@picsart/design-system/foundation/shadows';
import { Effects } from '@picsart/design-system/foundation/effects';
import { Easings } from '@picsart/design-system/foundation/easings';
import { Breakpoints } from '@picsart/design-system/foundation/responsive';
import { canHover } from '@picsart/design-system/styleUtils/hoverTouch';
import { BREAKPOINTS } from '@/consts/breakpoints';

const useStyles = createUseStyles(
  {
    root: {
      cursor: 'pointer',
      display: 'block',
      width: '100%',
      // Improve touch target
      minHeight: 44,
      WebkitTapHighlightColor: 'transparent',
      transition: `transform 0.2s ${Easings.easeOut}, box-shadow 0.2s ${Easings.easeOut}`,
      borderRadius: CornerRadius.r12,
      [canHover]: {
        '&:hover': {
          transform: 'scale(1.02)',
          boxShadow: Shadows.tint2,
        },
      },
      '&:active': {
        transform: 'scale(0.96)',
        boxShadow: Shadows.tint1,
      },
    },
    rootLoading: {
      cursor: 'default',
      [canHover]: {
        '&:hover': {
          transform: 'none',
          boxShadow: 'none',
        },
      },
      '&:active': {
        transform: 'none',
        opacity: 1,
      },
    },
    // Skeleton shimmer animation - GPU accelerated
    '@keyframes shimmer': {
      '0%': {
        transform: 'translateX(-100%)',
      },
      '100%': {
        transform: 'translateX(100%)',
      },
    },
    // Skeleton with title area
    skeletonWithTitle: {
      width: '100%',
      position: 'relative',
      backgroundColor: BackgroundColors.base,
      overflow: 'hidden',
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `linear-gradient(90deg, transparent 0%, ${BackgroundColors.tint2} 50%, transparent 100%)`,
        animation: `$shimmer 1.2s ${Easings.easeInOut} infinite`,
        willChange: 'transform',
      },
    },
    skeletonMedia: {
      width: '100%',
      paddingBottom: '100%', // 1:1 aspect ratio
    },
    skeletonTitle: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      padding: Spacings.s12,
      background: `linear-gradient(to top, ${Effects.alpha50} 0%, transparent 100%)`,
    },
    skeletonTitleBar: {
      height: 12,
      width: '70%',
      backgroundColor: Effects.alpha20,
      borderRadius: CornerRadius.r4,
    },
    mediaContainer: {
      width: '100%',
      aspectRatio: '1 / 1',
      minHeight: 100, // Minimum height for skeleton to show while loading
      borderRadius: CornerRadius.r12,
      overflow: 'hidden',
      position: 'relative',
      backgroundColor: BackgroundColors.base,
      [`@media (max-width: ${Breakpoints.xs - 1}px)`]: {
        borderRadius: CornerRadius.r8,
      },
      [`@media (max-width: ${BREAKPOINTS.narrow - 1}px)`]: {
        borderRadius: CornerRadius.r12,
      },
    },
    // LoadingCard container — a strict 1:1 square so the generating card has
    // the exact footprint of regular grid items. The compact Loading variant
    // scales its content down to fit this square (see Loading's useFitScale),
    // so nothing gets clipped by the pinned ratio.
    loadingCardContainer: {
      width: '100%',
      aspectRatio: '1 / 1',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: BackgroundColors.base,
      borderRadius: CornerRadius.r12,
      border: [1, 'solid', BackgroundColors.tint2],
      overflow: 'hidden',
      [`@media (max-width: ${Breakpoints.sm - 1}px)`]: {
        borderRadius: CornerRadius.r8,
      },
    },
  },
  { name: 'card-skeleton' }
);

export default useStyles;
